/**
 * Widgets slice sagas, ported from redux/apps/sagas.js (source of truth per
 * docs/widgets-migration/PARITY-AUDIT.md); the apps saga itself stays untouched.
 */
import { all, call, put, takeEvery } from 'redux-saga/effects'
import { withCallback } from 'redux-saga-callback'
import { push } from 'react-router-redux'
import { store as reduxStore } from 'index'
import _ from 'lodash'
import numberToWords from 'number-to-words'
import { isAuthErrorCode } from 'helpers'
import {
  list as chatWidgetListService,
  add as chatWidgetAdd,
  update as chatWidgetUpdate,
  getWidgetSettingsUrl,
  isWidgetApiError,
  ChatWidget,
  WidgetConfiguration,
  WidgetsListResponse,
  WidgetAddResponse,
  WidgetUpdateResponse,
  WIDGET_CONFIG_VERSION_V2,
} from 'services/chatWidgets'
import { list as extensionsList } from 'services/extensions'
import { RootState } from 'redux/types'
import actions from './actions'
import {
  buildDefaultWidgetConfiguration,
  buildMainSettings,
  buildSidebarSettings,
  buildStyleSettings,
} from './widgetConfig'

const getRoot = (): RootState => reduxStore.getState() as unknown as RootState

type Saga<P> = { payload?: P }

// See redux/users/sagas.ts — redux-saga@1's take* overloads can't infer a worker
// narrowed to `{ payload }`, so workers are cast at the registration boundary only.
type SagaWorker = (action: any) => any

const EMPTY_LIST_STATE = {
  chatWidgetsList: [],
  chatWidgetsCombinedList: [],
  chatWidgetAppUrl: false,
  chatWidgetActive: [],
  areWidgetsLoading: false,
}

/** Only v2 widgets are editable; partial records exist, so guard rather than assume. */
const isEditableWidget = (widget: ChatWidget): boolean =>
  widget?.configuration?.version === WIDGET_CONFIG_VERSION_V2

const notify = (text: string, type: 'success' | 'error'): void => {
  if (typeof window.triggerNotification === 'function') window.triggerNotification(text, type)
}

/** Surfaces the API's own message when it sent one, else a generic fallback. */
function notifyApiError(response: unknown, fallback: string): void {
  if (isWidgetApiError(response)) {
    const { code, message: apiMessage, details } = response.error
    if (code === 'ERR_BAD_REQUEST' && details) {
      const messages = Object.values(details)
      if (messages.length) {
        messages.forEach(value => notify(String(value), 'error'))
        return
      }
    }
    if (apiMessage) {
      notify(apiMessage, 'error')
      return
    }
  }
  notify(fallback, 'error')
}

export interface ChatWidgetsListPayload {
  /** Selects this widget as active once the list resolves. */
  id?: string
  /** Set by ADD_CHAT_WIDGET — redirects to the new widget's detail view. */
  redirectToCustomize?: boolean
  /** Skips the extensions fetch when the caller already knows the widget host. */
  knownAppUrl?: string | false
}

export function* CHAT_WIDGETS_LIST({ payload }: Saga<ChatWidgetsListPayload>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: true } })

    const appData = getRoot().apps.active

    let chatWidgetsList: ChatWidget[] = []
    let chatWidgetsCombinedList: ChatWidget[] = []
    let chatWidgetActive: ChatWidget | unknown[] = []

    // Saving a widget cannot change which extensions are installed, so a caller
    // that already has the host skips re-deriving it.
    let chatWidgetAppUrl = payload?.knownAppUrl
    if (!chatWidgetAppUrl) {
      const allExtensions = yield call(extensionsList, appData, 'installed')

      if (!allExtensions || !allExtensions.data) {
        yield put({ type: actions.SET_STATE, payload: EMPTY_LIST_STATE })
        const errorCode = allExtensions?.error?.code
        if (errorCode && isAuthErrorCode(errorCode)) {
          if (errorCode === 'AUTH_ERR_TOKEN_BLACKLISTED') yield put({ type: 'user/LOGOUT' })
          else yield put(push('/login'))
        }
        return true
      }

      chatWidgetAppUrl = getWidgetSettingsUrl(allExtensions.data)
    }

    if (chatWidgetAppUrl) {
      const response: WidgetsListResponse = yield call(
        chatWidgetListService,
        appData,
        chatWidgetAppUrl,
      )
      if (response && response.data) {
        chatWidgetsCombinedList = response.data
        chatWidgetsList = response.data.filter(isEditableWidget)
      }

      const { id } = payload || {}
      if (id) {
        chatWidgetActive = chatWidgetsList.find(widget => widget.widgetId === id) || []
      }
    }

    yield put({
      type: actions.SET_STATE,
      payload: {
        chatWidgetsList,
        chatWidgetsCombinedList,
        chatWidgetAppUrl,
        chatWidgetActive,
        areWidgetsLoading: false,
      },
    })

    const appId = appData?.id
    if (payload?.redirectToCustomize && payload.id) {
      yield put(push(`/app/${appId}/widgets/${payload.id}/detail`))
    } else if (payload?.id && _.isEmpty(chatWidgetActive)) {
      yield put(push(`/app/${appId}/widgets`))
    }

    return chatWidgetActive
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: EMPTY_LIST_STATE })
    notify('Failed to load widgets', 'error')
    return true
  }
}

export interface GetChatWidgetPayload {
  id?: string
  data?: { id?: string }
}

export function* GET_CHAT_WIDGET({ payload }: Saga<GetChatWidgetPayload>) {
  const appId = payload?.data?.id || getRoot().apps.active?.id
  try {
    const { chatWidgetsList } = getRoot().widgets
    const found = chatWidgetsList.find(widget => widget.widgetId === payload?.id)

    // The legacy handler assigned `widgetId` onto the found object, mutating the
    // list entry in place. Selecting it is enough — the id is already on the record.
    const chatWidgetActive: ChatWidget | unknown[] = found || []

    yield put({ type: actions.SET_STATE, payload: { chatWidgetActive } })

    // isEmpty, not a length check — chatWidgetActive can be an object, not an array.
    if (_.isEmpty(chatWidgetActive)) {
      notify('This widget can no longer be edited', 'error')
      yield put(push(`/app/${appId}/widgets`))
    }
  } catch (e) {
    notify('Failed to open widget', 'error')
    yield put(push(`/app/${appId}/widgets`))
  }
}

export function* ADD_CHAT_WIDGET() {
  try {
    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: true } })

    // A click landing before the page's list fetch resolves would post ids: []
    // and name the widget as if it were the first one.
    if (!getRoot().widgets.chatWidgetAppUrl) {
      yield call(CHAT_WIDGETS_LIST, { payload: {} } as Saga<ChatWidgetsListPayload>)
    }

    const { chatWidgetAppUrl, chatWidgetsList, chatWidgetsCombinedList } = getRoot().widgets
    const activeApp = getRoot().apps.active
    const name = `My ${numberToWords.toWordsOrdinal(chatWidgetsList.length + 1)} chat`

    const post = {
      ids: chatWidgetsCombinedList.map(widget => widget.widgetId),
      settings: buildDefaultWidgetConfiguration(activeApp, name),
    }

    const response: WidgetAddResponse = yield call(
      chatWidgetAdd,
      post,
      activeApp,
      chatWidgetAppUrl || '',
    )

    // A malformed create still answers 200 { data: { widgetId: null } }, so a truthy
    // `data` is not proof of success — require the id itself.
    const newWidgetId = response?.data?.widgetId
    if (newWidgetId) {
      yield put({
        type: actions.CHAT_WIDGETS_LIST,
        payload: { id: newWidgetId, redirectToCustomize: true, knownAppUrl: chatWidgetAppUrl },
      })
      notify('Widget created', 'success')
      return response
    }

    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: false } })
    notifyApiError(response, 'Failed to create widget')
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: false } })
    notify('Failed to create widget', 'error')
    return false
  }
}

export interface UpdateChatWidgetPayload {
  selectedComponent: 'GLOBAL' | 'SIDEBAR' | 'MAIN'
  values: Record<string, unknown>
  chatWidgetAppUrl?: string | false
}

export function* UPDATE_CHAT_WIDGET({ payload }: Saga<UpdateChatWidgetPayload>) {
  try {
    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: true } })

    const { chatWidgetActive, chatWidgetAppUrl } = getRoot().widgets
    const activeApp = getRoot().apps.active
    const { widgetId } = chatWidgetActive as ChatWidget
    const { selectedComponent, values } = payload || ({} as UpdateChatWidgetPayload)

    // Without an id the API UPSERTs a partial record rather than failing, silently
    // creating a junk widget. Refuse to send instead.
    if (!widgetId) {
      yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: false } })
      notify('Failed to save widget', 'error')
      return false
    }

    // Cloned so this doesn't mutate redux state before the request resolves.
    const settings: WidgetConfiguration = {
      ...((chatWidgetActive as ChatWidget).configuration || {}),
      widgetId,
    }

    if (selectedComponent === 'GLOBAL') {
      settings.style = buildStyleSettings(values, settings.style)
      settings.name = values.name as string
    } else if (selectedComponent === 'SIDEBAR') {
      settings.sidebar = buildSidebarSettings(values, settings.sidebar)
    } else if (selectedComponent === 'MAIN') {
      settings.main = buildMainSettings(values, settings.main)
    }

    const response: WidgetUpdateResponse = yield call(
      chatWidgetUpdate,
      { settings },
      activeApp,
      payload?.chatWidgetAppUrl || chatWidgetAppUrl || '',
    )

    // Explicit === true: the service can resolve a truthy value on failure too.
    if (response?.success === true) {
      yield put({
        type: actions.CHAT_WIDGETS_LIST,
        payload: { id: widgetId, knownAppUrl: payload?.chatWidgetAppUrl || chatWidgetAppUrl },
      })
      notify('Widget saved', 'success')
      return response
    }

    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: false } })
    notifyApiError(response, 'Failed to save widget')
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areWidgetsLoading: false } })
    notify('Failed to save widget', 'error')
    return false
  }
}

export default function* rootSaga() {
  yield all([
    // withCallback wraps mirror redux/apps/sagas.js:20794-20797 — GET_CHAT_WIDGET is
    // deliberately the only unwrapped one there, so callers can await the other three.
    takeEvery(actions.CHAT_WIDGETS_LIST, withCallback(CHAT_WIDGETS_LIST) as SagaWorker),
    takeEvery(actions.GET_CHAT_WIDGET, GET_CHAT_WIDGET as SagaWorker),
    takeEvery(actions.ADD_CHAT_WIDGET, withCallback(ADD_CHAT_WIDGET) as SagaWorker),
    takeEvery(actions.UPDATE_CHAT_WIDGET, withCallback(UPDATE_CHAT_WIDGET) as SagaWorker),
  ])
}
